/* global React */
import type { Config } from './config';
import type { BellaTranslator } from './bellaTranslator';
import type { FeatureToggles } from './featureToggles';
import type { Logger } from './loggerTypes';
import type { NavigationPush, PageTypeResult, Route } from './routingTypes';
import type { TrackingHelpers } from './trackingHelpers';
import type { AbstractAppBridge } from './appBridgeTypes';
import type { AppShellLoginState } from './loginStateTypes';
import type { AppshellMeta } from './appshellMetaTypes';
import type { UseUniversalFetchType } from './useUniversalFetch';
import { FetchingHelpers } from './fetchingHelpers';
import { ConfigurationsState } from './remoteConfigType';
import { CookieConsentType } from './cookieConsentType';
import { UseAppshellCookies } from './useCookieType';

export interface MicroFrontendAppShellProps {
    t: BellaTranslator;
    featureToggles: FeatureToggles;
    logger: Logger;
    routingHelpers: {
        host: string;
        HybridLink: React.FunctionComponent;
        useBellaPush: NavigationPush;
        routes: Record<Route, string>;
        useCurrentPageType: () => PageTypeResult;
    };
    trackingHelpers: TrackingHelpers;
    config: Config;
    remoteConfig: ConfigurationsState;
    AppBridge: typeof AbstractAppBridge;
    loginState: AppShellLoginState;
    appshellMeta: AppshellMeta;
    useUniversalFetch: UseUniversalFetchType;
    useCookies: UseAppshellCookies<any, any>;
    fetchingHelpers: FetchingHelpers;
    fetchedUniversalState: any;
}

export interface SignupAndLoginProps {
    ShopToClubOverlay: React.ComponentType<MicroFrontendAppShellProps>;
    ClubSignupAndLogin: React.ComponentType<MicroFrontendAppShellProps>;
    SignupAndLoginProvider: React.ComponentType<MicroFrontendAppShellProps>;
    MagicLinkSent: React.ElementType;
    MagicLinkForm: React.ElementType;
    ResetPasswordForm: React.ElementType;
    ResetPasswordSent: React.ElementType;
    PartialLoginWrapper: React.ElementType;
    LoginAndRegister: React.ElementType;
    AccountTransferConfirm: React.ElementType;
}

export type SecureMicroFrontendAppShellProps = Omit<
    MicroFrontendAppShellProps,
    'routingHelpers' | 'trackingHelpers'
> & {
    cookieConsent: CookieConsentType;
};

export type ClubMicroFrontendAppShellProps = Omit<MicroFrontendAppShellProps, 'trackingHelpers'> & {
    cookieConsent: CookieConsentType;
};

export interface CheckoutProps {
    Main: React.ComponentType<MicroFrontendAppShellProps>;
    SecurePaymentPage: React.ComponentType<SecureMicroFrontendAppShellProps>;
}

export interface OwwHeaderTabNavItem {
    linkText: string;
    linkUrl: string;
    isSelected: boolean;
    trackingAction: string;
}

export interface BurgerMenuConfig {
    onClick?: () => void;
    dataTestId?: string;
    className?: string;
    handleBurgerMenuCloseClick?: () => void;
}

type IconTrayComponents = React.ReactNode[];

export interface MiniHeaderConfig {
    iconTrayComponents?: IconTrayComponents;
    handleBackButtonClick: () => void;
    backButtonTestId?: string;
}

export interface LogoConfig {
    linkComponent?: React.ElementType;
    handleBurgerMenuCloseClick?: () => void;
}

export enum HeaderModeType {
    DEFAULT = 'default',
    FIRST_ROW = 'first_row',
    SECOND_ROW = 'second_row',
    MINI = 'mini',
    NONE = 'none',
}

export interface OwwHeaderProps {
    middleAreaComponent?: React.ReactNode;
    iconTrayComponents?: IconTrayComponents;
    burgerMenuConfig?: BurgerMenuConfig;
    tabsConfig?: OwwHeaderTabNavItem[];
    miniHeaderConfig?: MiniHeaderConfig;
    headerMode?: HeaderModeType;
    infoBlockComponents?: React.ReactNode[];
    onStickyChange?: (isStuck: boolean) => void;
    stickyAreaComponents?: React.ReactNode;
    afterHeaderComponent?: React.ReactNode;
    shouldShowAppBanner?: boolean;
    hasBarBanner?: boolean;
    logoConfig?: LogoConfig;
    showOnboardingTooltip?: boolean;
    featureToggles?: FeatureToggles;
}
